import memoize from 'memoizee';
//import { captureException } from '@sentry/react';
import { cloneDeep, isEmpty, uniq } from 'lodash';
import { TEXTURE_MAP } from '../../../config/constant/unityConstants';
import { DEFAULT_SORT_BY, OPTION_TYPE } from '../../../constants/productConfiguratorConstants';
import {
  apiGet,
  apiGetMaterialTextures,
  apiGetMaterialsV2,
  apiGetTexture,
  apiGetTextureList,
  apiPatch,
} from '../../../helpers/api';
import { dispatcher } from '../../../helpers/projectHelper';
import { errorToastV2, infoToastV2 } from '../../../helpers/toastHelper';
import { waitForEventOnce } from '../../../helpers/unityPubSub';
import {
  configuratorUnityObjectPropertiesActions,
  selectApplyMaterialInProgress,
  selectArePropertiesChanged,
} from '../../../redux/slicers/configurator/configuratorUnityObjectProperties.slicer';
import { getAppState } from '../../../redux/store';
import { CONFIGURATOR_UNITY_EVENT } from './unityCallbacks';
import { applyStyleLastInfo, configuratorMaterialModule } from './unityMethods';
import { ONE_MINUTE } from '../../../constants/constants';
import {
  selectSelectedOption,
  selectSortedLabelOptions,
} from '../../../redux/slicers/configurator/configuratorLabelDetails.slicer';
import { openYesNoDialog } from '../../../hooks/useInfoDialogaActions';
import {
  getMaterial,
  getMaterialTexture,
  getTexture,
  updateDbMaterial,
  updateDbMaterialTextures,
  updateDbTexture,
} from '../../../helpers/idb';
import {
  openEditOption,
  openSceneLabels,
} from '../../../redux/slicers/configurator/configurator.actions';
import {
  selectApplyTextureInProgress,
  setApplyTextureInProgress,
} from '../../../redux/slicers/admin/curatorStylesSlicer';
import { selectLabelById } from '../../../redux/slicers/configurator/configuratorSceneLabels.slicer';
import {
  selectBaseMaterialData,
  selectConfiguratorId,
  selectConfiguratorSortBy,
  selectSelectedLabelId,
  // selectSelectedOption as selectedOptionConfig,
  selectSelectedOptionId,
  selectSelectedSceneId,
  selectUnitySceneLoaded,
} from '../../../redux/slicers/configurator/configurator.slicer';
import { getLabelOptions, updateOptionApi } from '../../../api/configuratorApi';
import {
  GET_LABEL_OPTIONS_API,
  GET_LABEL_OPTIONS_API_V2,
  UPDATE_LABEL_OPTIONS_API,
  UPDATE_LABEL_OPTIONS_API_V2,
} from '../../../config/constant/apiConstant';
import { fetchAllPages } from '../../../helpers/apiHelper';
import { undoRedo } from '../../admin/curator/UndoRedo/UndoRedo';
import { isV2, resetPathIfIdIsNotPresent } from '../utils';

const checkIfMaterialInProgress = () => {
  if (selectApplyMaterialInProgress(getAppState())) {
    errorToastV2('Please wait, previous material is still in progress', {
      toastId: 'MATERIAL_APPLY_IN_PROGRESS',
    });
    return true;
  }
  return false;
};

const checkIfCanApply = () => {
  if (selectApplyTextureInProgress(getAppState())) {
    errorToastV2('Please wait, previous texture is still in progress', {
      toastId: 'TEXTURE_APPLY_IN_PROGRESS',
    });
    return false;
  }
  if (checkIfMaterialInProgress()) {
    return false;
  }
  return true;
};

export const fetchTextureMemoized = memoize(
  (textureId) => {
    // TODO: remove memoize
    // TODO: save texture data in the store
    // TODO: when user refetches texture -> if it fails show error with button refetch
    // TODO: if user refetches and it success -> apply OPTION one more time -> not the texture but option as while data was refetching we might switched the option
    // TODO: check if current option connected to texture which were refetched -> if it's connected then apply it to the object
    // TODO: always show a message that texture refetched successfully
    return apiGetTexture({ id: textureId });
  },
  {
    length: 1,
    maxAge: 300 * ONE_MINUTE,
  }
);

export const applyTexture = async ({
  texture,
  option,
  material,
  mapType = TEXTURE_MAP.DIFFUSE,
  baseMaterialData,
  labelId,
  isUpdate = true,
  skipHistory = false,
}) => {
  if (!texture) return false;
  if (!option) {
    labelId = labelId || selectSelectedLabelId(getAppState());
    const options = selectSortedLabelOptions(labelId)(getAppState());
    const optionId = selectSelectedOptionId(getAppState());
    option = options.find((val) => val.id === optionId);
  }
  if (mapType === TEXTURE_MAP.DIFFUSE) {
    // we're changing texture, need to validate if it's possible to apply
    if (!checkIfCanApply()) {
      return false;
    }
  } else {
    // we're chaning custom map -> texture in progress validation is already checked so we just need to check if we can apply material
    if (checkIfMaterialInProgress()) {
      return false;
    }
  }
  dispatcher(setApplyTextureInProgress(true));
  if (typeof texture === 'number') {
    // we've got textre id so fetching the data
    texture = option?.texture_data || (await fetchTextureMemoized(texture));
  }
  if (!texture?.id) {
    errorToastV2('Unable apply texture. Texture details are missing');
    dispatcher(setApplyTextureInProgress(false));
    return;
  }
  if (material && typeof material === 'number') {
    // TODO: handle errors
    material = option?.material_data || (await fetchMaterialMemoized(material));
  }
  try {
    await waitForEventOnce(CONFIGURATOR_UNITY_EVENT.OnConfiguratorStyleApplied, async () => {
      await configuratorMaterialModule.ApplyStyleReceiver({
        texture,
        option,
        material,
        mapType,
        labelId,
        baseMaterialData,
      });
    });
    const objectProperties = await waitForEventOnce(
      CONFIGURATOR_UNITY_EVENT.OnReceiveLabelObjectProperties,
      () => {
        configuratorMaterialModule.GetObjectDataByLabelId({ labelId });
      }
    );
    if (isUpdate) {
      let obj = {};
      if (option?.material_properties) {
        obj = {
          copiedFromMaterialName: option?.material_properties?.copiedFromMaterialName,
          copiedFromObjectName: option?.material_properties?.copiedFromObjectName,
        };
      }
      const updateData = {
        id: option.id,
        is_selected: true,
        material_properties: { ...objectProperties, ...obj },
      };
      await dispatcher(
        updateOptionApi({
          labelId,
          optionDataId: option?.data?.id,
          option: updateData,
        })
      );
    }
    if (!skipHistory) {
      await addActionInUndoRedo({ labelId, materialProperies: objectProperties, option });
    }
  } catch (error) {
    console.error(error);
    //captureException(error);
    dispatcher(setApplyTextureInProgress(false));
    errorToastV2('The texture is not available. Please contact support. LF_C_8993283');
    return;
  }
  dispatcher(setApplyTextureInProgress(false));
};

export const applyColor = async ({
  color,
  option,
  material,
  baseMaterialData,
  labelId,
  skipHistory = false,
  isUpdate = true,
}) => {
  if (!color) return false;
  if (!option) {
    const options = selectSortedLabelOptions(labelId)(getAppState());
    const optionId = selectSelectedOptionId(getAppState());
    option = options.find((val) => val.id === optionId);
  }
  if (!checkIfCanApply()) {
    return false;
  }
  dispatcher(setApplyTextureInProgress(true));
  if (material && typeof material === 'number') {
    // TODO: handle errors
    material = option?.material_data || (await fetchMaterialMemoized(material));
  }
  try {
    await waitForEventOnce(CONFIGURATOR_UNITY_EVENT.OnConfiguratorStyleApplied, async () => {
      await configuratorMaterialModule.ApplyStyleReceiver({
        color,
        option,
        material,
        baseMaterialData,
        labelId,
      });
    });
    const objectProperties = await waitForEventOnce(
      CONFIGURATOR_UNITY_EVENT.OnReceiveLabelObjectProperties,
      () => {
        configuratorMaterialModule.GetObjectDataByLabelId({ labelId });
      }
    );
    if (isUpdate) {
      let obj = {};
      if (option?.material_properties) {
        obj = {
          copiedFromMaterialName: option?.material_properties?.copiedFromMaterialName,
          copiedFromObjectName: option?.material_properties?.copiedFromObjectName,
        };
      }
      const updateData = {
        id: option.id,
        color_code: objectProperties.colorValue,
        is_selected: true,
        material_properties: { ...objectProperties, ...obj },
      };
      await dispatcher(
        updateOptionApi({
          labelId,
          optionDataId: option?.data?.id,
          option: updateData,
        })
      );
    }
    if (!skipHistory) {
      await addActionInUndoRedo({ labelId, materialProperies: objectProperties, option });
    }
  } catch (e) {
    console.error(e);
    //captureException(e);
    errorToastV2('Unable apply color. LF_C_2423488', {
      toastId: `COLOR_APPLY_ERROR`,
    });
  }
  dispatcher(setApplyTextureInProgress(false));
};

export const fetchMaterialMemoized = memoize(
  // TODO: replace it with a call to fetch only 1 material
  async (materialId) => {
    const result = await apiGetMaterialsV2({ query: `id=${materialId}` });
    const material = result?.results?.[0];
    return material;
  },
  { length: 1, maxAge: 300 * ONE_MINUTE }
);

export const applyMaterial = async ({
  material,
  option,
  labelId,
  skipHistory = false,
  isUpdate = true,
}) => {
  if (!material) return false;
  if (!option) {
    const options = selectSortedLabelOptions(labelId)(getAppState());
    const optionId = selectSelectedOptionId(getAppState());
    option = options.find((val) => val.id === optionId);
  }
  if (!checkIfCanApply()) {
    return false;
  }
  dispatcher(configuratorUnityObjectPropertiesActions.setApplyMaterialInProgress(true));
  if (typeof material === 'number') {
    // we've got material id so fetching the data
    material = option?.material_data || (await fetchMaterialMemoized(material));
  }
  if (!material?.material_image && !material?.file) {
    errorToastV2('Unable apply material. Material details are missing', {
      toastId: `MATERIAL_IMAGE_MISSING_${material.id}`,
    });
    dispatcher(configuratorUnityObjectPropertiesActions.setApplyMaterialInProgress(false));
    return;
  }
  try {
    await waitForEventOnce(CONFIGURATOR_UNITY_EVENT.OnConfiguratorStyleApplied, async () => {
      await configuratorMaterialModule.ApplyStyleReceiver({ material, option, labelId });
    });
    const objectProperties = await waitForEventOnce(
      CONFIGURATOR_UNITY_EVENT.OnReceiveLabelObjectProperties,
      () => {
        configuratorMaterialModule.GetObjectDataByLabelId({ labelId });
      }
    );
    if (isUpdate) {
      let obj = {};
      if (option?.material_properties) {
        obj = {
          copiedFromMaterialName: option?.material_properties?.copiedFromMaterialName,
          copiedFromObjectName: option?.material_properties?.copiedFromObjectName,
        };
      }
      const updateData = {
        id: option.id,
        is_selected: true,
        material_properties: { ...objectProperties, ...obj },
      };
      await dispatcher(
        updateOptionApi({
          labelId,
          optionDataId: option?.data?.id,
          option: updateData,
        })
      );
    }
    if (!skipHistory) {
      await addActionInUndoRedo({ labelId, materialProperies: objectProperties, option });
    }
  } catch (e) {
    console.error(e);
    //captureException(e);
    errorToastV2('Unable apply material. LF_C_2423488', {
      toastId: `MATERIAL_APPLY_ERROR_${material.id}`,
    });
  }
  dispatcher(configuratorUnityObjectPropertiesActions.setApplyMaterialInProgress(false));
};

const addActionInUndoRedo = async ({ labelId, materialProperies, option }) => {
  const sceneId = selectSelectedSceneId(getAppState());
  // const selectedOption = selectedOptionConfig(getAppState());
  const options = selectSortedLabelOptions(labelId)(getAppState());
  const previousSelectedOption = options?.find((val) => val?.data?.is_selected || val?.is_selected);
  await undoRedo.objectProperties.applyMaterialTextureColorInConfigurator({
    material: materialProperies,
    previousSelectedOption,
    currentSelectedOption: option,
    sceneId,
  });
};

export const validateUndoRedo = ({ option, sceneId, labelId }) => {
  const options = selectSortedLabelOptions(labelId)(getAppState());
  const isOptionPresent = options.some((val) => val.id === option.id);
  const currentSceneId = selectSelectedSceneId(getAppState());
  if (!isOptionPresent || currentSceneId !== sceneId) {
    return false;
  }
  return true;
};

const fetchLfTexturesMemoized = memoize(
  (query) => {
    // TODO: remove memoize
    // TODO: save texture data in the store
    // TODO: when user refetches texture -> if it fails show error with button refetch
    // TODO: if user refetches and it success -> apply OPTION one more time -> not the texture but option as while data was refetching we might switched the option
    // TODO: check if current option connected to texture which were refetched -> if it's connected then apply it to the object
    // TODO: always show a message that texture refetched successfully
    return apiGetTextureList({ query });
  },
  {
    length: 1,
    maxAge: 300 * ONE_MINUTE,
  }
);

export const fetchMaterialTexturesMemoized = memoize(
  (query) => {
    // TODO: remove memoize
    // TODO: save texture data in the store
    // TODO: when user refetches texture -> if it fails show error with button refetch
    // TODO: if user refetches and it success -> apply OPTION one more time -> not the texture but option as while data was refetching we might switched the option
    // TODO: check if current option connected to texture which were refetched -> if it's connected then apply it to the object
    // TODO: always show a message that texture refetched successfully
    return apiGetMaterialTextures(query);
  },
  {
    length: 1,
    maxAge: 300 * ONE_MINUTE,
  }
);

export const updateMissingTexturesInDb = async ({ option, materialProperies }) => {
  const missingTextures = [];
  const object = materialProperies?.changedMapsPath || {};
  let textureIds = [
    object?.AOId,
    object?.diffuseId,
    object?.metallicId,
    object?.normalId,
    object?.roughnessId,
    object?.transparencyId,
  ].filter(Boolean);
  textureIds = uniq(textureIds);
  const materialId = materialProperies?.loadedMaterialId;
  const textureId = materialProperies?.textureId;
  const materialTextureId = materialProperies?.materialTextureId;
  const storedTextureId = option?.texture_data?.id;
  const storedMaterialId = option?.material_data?.id;
  if (
    textureId &&
    storedTextureId &&
    /^\d+$/.test(textureId) &&
    parseInt(textureId) === parseInt(storedTextureId)
  ) {
    const index = textureIds.findIndex((id) => parseInt(id) === parseInt(storedTextureId));
    if (index !== -1) {
      textureIds.splice(index, 1);
    }
    const texture = option?.texture_data;
    await updateDbTexture(texture);
  }
  await Promise.all(
    textureIds.map(async (textureId) => {
      const isNumber = /^\d+$/.test(textureId);
      if (isNumber) {
        const texture = await getTexture(parseInt(textureId));
        !texture && missingTextures.push(textureId);
      }
    })
  );
  if (missingTextures.length) {
    const query = `id=${missingTextures.join(',')}&ignore_lf_default=true&per_page=20`;
    const textures = await fetchAllPages((page) => {
      return fetchLfTexturesMemoized(`${query}&page=${page}`);
    });
    if (textures?.results?.length) {
      await Promise.all(
        textures.results.map((texture) => {
          return updateDbTexture(texture);
        })
      );
    }
  }
  if (materialId && /^\d+$/.test(materialId)) {
    if (storedMaterialId && parseInt(materialId) === parseInt(storedMaterialId)) {
      const storedMaterial = option?.material_data;
      await updateDbMaterial(storedMaterial);
    } else {
      const materialIndexedDb = await getMaterial(parseInt(materialId));
      if (!materialIndexedDb) {
        const material = await fetchMaterialMemoized(materialId);
        material && (await updateDbMaterial(material));
      }
    }
  }
  if (materialTextureId && /^\d+$/.test(materialTextureId)) {
    const materialTextureIndexedDb = await getMaterialTexture(parseInt(materialTextureId));
    if (!materialTextureIndexedDb) {
      const materialsResponse = await fetchMaterialTexturesMemoized(
        `old_texture_id=${materialTextureId}`
      );
      if (materialsResponse?.results?.length) {
        const materialTexture = materialsResponse.results[0];
        materialTexture && (await updateDbMaterialTextures(materialTexture));
      }
    }
  }
};

export const applyMaterialProperties = async ({
  materialProperies,
  labelId,
  option,
  isUpdate = true,
  skipHistory = false,
}) => {
  dispatcher(configuratorUnityObjectPropertiesActions.setApplyMaterialInProgress(true));
  await updateMissingTexturesInDb({ materialProperies, option });
  await waitForEventOnce(CONFIGURATOR_UNITY_EVENT.OnConfiguratorStyleApplied, async () => {
    const _materialProperties = cloneDeep(materialProperies);
    const updatedMaterialProperties = resetPathIfIdIsNotPresent(_materialProperties);
    if (!skipHistory) {
      await addActionInUndoRedo({ labelId, materialProperies: updatedMaterialProperties, option });
    }
    await configuratorMaterialModule.ApplyStyleReceiver({
      defaultObjectData: updatedMaterialProperties,
      labelId,
      option,
    });
    if (isUpdate && (isV2() ? !option?.data?.is_selected : !option?.is_selected)) {
      const updateData = {
        id: option.id,
        is_selected: true,
      };
      await dispatcher(
        updateOptionApi({
          labelId,
          optionDataId: option?.data?.id,
          option: updateData,
        })
      );
    }
  });
  dispatcher(configuratorUnityObjectPropertiesActions.setApplyMaterialInProgress(false));
};

export const applyOption = async ({
  option,
  edit = false,
  skipHistory = false,
  cancelMapSelection,
}) => {
  const sceneLoaded = selectUnitySceneLoaded(getAppState());
  if (!sceneLoaded) {
    infoToastV2('Please wait, scene is still loading...');
    return;
  }
  const labelId = option.sceneoption;
  const sceneId = selectSelectedSceneId(getAppState());
  const label = selectLabelById(labelId, sceneId)(getAppState());
  if (!label) {
    errorToastV2('Label is missing');
    return;
  }
  if (isEmpty(label.data?.linkedObjects)) {
    errorToastV2('Link some objects to label before applying an option', {
      toastId: 'APPLY_OPTION_NO_LINKED_OBJECTS',
      actions: [
        {
          label: 'Link objects',
          onClick: () => openSceneLabels(),
        },
      ],
    });
    return;
  }
  // TODO: for now we accept `cancelMapSelection` as paramter as it's connected to context but we need to handle it somehow so we don't need to
  const currentEditOption = selectSelectedOption(getAppState());
  if (currentEditOption) {
    const isFromSameLabel = currentEditOption.sceneoption === option.sceneoption;
    if (isFromSameLabel || edit) {
      const dialogProps = {
        onButtonClick: async () => {
          cancelMapSelection && (await cancelMapSelection());
          await forceApplyOption({ option });
        },
        theme: 'v2',
        closeable: false,
      };
      if (currentEditOption.id === option.id) {
        openYesNoDialog({
          ...dialogProps,
          title: 'Reset option?',
          description: 'All unsaved changes will be overwritten',
        });
        return;
      } else {
        const arePropertiesChanged = selectArePropertiesChanged(getAppState());
        if (!arePropertiesChanged) {
          openEditOption({ optionId: option.id, labelId: option.sceneoption });
          return;
        }
        openYesNoDialog({
          ...dialogProps,
          title: `Apply option ${option.display_name}?`,
          description: 'All unsaved changes will be overwritten',
          onButtonClick: async () => {
            openEditOption({ optionId: option.id, labelId: option.sceneoption, confirmed: true });
          },
        });
        return;
      }
    }
  }
  if (edit) {
    openEditOption({ optionId: option.id, labelId: option.sceneoption });
    return;
  }
  await forceApplyOption({ option, skipHistory });
};

export const forceApplyOption = async ({ option, skipHistory = false, isUpdate = true }) => {
  applyStyleLastInfo.labelId = null;
  applyStyleLastInfo.lastApplied = null;
  const labelId = option.sceneoption;
  if (!isEmpty(option.material_properties)) {
    await applyMaterialProperties({
      materialProperies: option.material_properties,
      labelId,
      option,
      skipHistory,
      isUpdate,
    });
    return;
  }
  const material = option.material_data || option.material;
  const texture = option.texture_data || option.texture;
  const baseMaterialData = option?.extra_data?.baseMaterialData;
  switch (option.texture_type) {
    case OPTION_TYPE.COLOR:
      await applyColor({
        color: option.color_code,
        option,
        material,
        labelId,
        baseMaterialData,
        skipHistory,
        isUpdate,
      });
      break;
    case OPTION_TYPE.MATERIAL:
      await applyMaterial({ material, option, labelId, skipHistory, isUpdate });
      break;
    case OPTION_TYPE.TEXTURE:
      await applyTexture({
        texture,
        option,
        material,
        labelId,
        baseMaterialData,
        skipHistory,
        isUpdate,
      });
      break;
    default:
      throw new Error('Option type is not supported ' + option.texture_type);
  }
};

export const resetBaseMaterialOnObjectDelete = async (objects) => {
  const sceneId = selectSelectedSceneId(getAppState());
  const baseMaterialData = selectBaseMaterialData(getAppState());
  // const selectedOption = selectedOptionConfig(getAppState());
  const sortBy = selectConfiguratorSortBy(getAppState());
  let url = `${isV2() ? GET_LABEL_OPTIONS_API_V2 : GET_LABEL_OPTIONS_API}?scene=${sceneId}`;
  if (sortBy !== DEFAULT_SORT_BY) {
    url = `${url}&sort=${sortBy}`;
  }
  const result = await fetchAllPages((page) => {
    const paginatedUrl = `${url}&page=${page}`;
    return apiGet(paginatedUrl);
  });
  const options = result?.results;
  if (options?.length) {
    const optionsToUpdate = [];
    options.map((val) => {
      const baseMaterial = val?.extra_data?.baseMaterialData;
      const objectName = baseMaterial?.blenderObjectName || baseMaterial?.objectName;
      if (baseMaterial && objects.indexOf(objectName) !== -1) {
        const obj = {
          id: val.id,
          material_properties: {},
          extra_data: {},
          material: baseMaterialData.id,
        };
        if (isV2()) {
          obj.configurator_id = selectConfiguratorId(getAppState());
          obj.data = {
            id: val?.data?.id,
            scene_id: val?.data?.scene,
          };
        }
        optionsToUpdate.push(obj);
      }
      return val;
    });
    if (optionsToUpdate.length) {
      const result = await apiPatch(
        isV2() ? UPDATE_LABEL_OPTIONS_API_V2 : UPDATE_LABEL_OPTIONS_API,
        JSON.stringify(optionsToUpdate),
        'updateBaseMaterial',
        null,
        null,
        false
      );
      const data = result?.data;
      if (data?.length) {
        let labelIds = data.map((val) => val?.sceneoption);
        labelIds = uniq(labelIds);
        await Promise.all(
          labelIds.map(async (labelId) => {
            await dispatcher(getLabelOptions({ labelId, onlyOnce: false, onlyRedux: true }));
          })
        );
        await Promise.all(
          data.map(async (val) => {
            if (val?.data?.is_selected || val?.is_selected) await forceApplyOption({ option: val });
          })
        );
      }
    }
  }
};
